import React from 'react';
import { useNavigate } from 'react-router-dom';
import useFavoritesStore from '../store/useFavoritesStore';
import useCurrencyStore, { formatPrice } from '../store/useCurrencyStore';

const CATEGORY_COLORS = {
  MODELS: 'bg-rose-500/20 text-rose-400',
  VOICEOVER: 'bg-teal-500/20 text-teal-400',
  DESIGN_BRANDING: 'bg-pink-500/20 text-pink-400',
  MARKETING_SOCIAL: 'bg-orange-500/20 text-orange-400',
  VIDEO_PHOTOGRAPHY: 'bg-blue-500/20 text-blue-400',
  WEB_DEVELOPMENT: 'bg-green-500/20 text-green-400',
  CONTENT_CREATION: 'bg-yellow-500/20 text-yellow-400',
  STUDIO_RENTALS: 'bg-purple-500/20 text-purple-400',
  EVENT_PLANNING: 'bg-amber-500/20 text-amber-400',
};

const CATEGORY_LABELS = {
  MODELS: 'Models',
  VOICEOVER: 'Voiceover',
  DESIGN_BRANDING: 'Design',
  MARKETING_SOCIAL: 'Marketing',
  VIDEO_PHOTOGRAPHY: 'Video',
  WEB_DEVELOPMENT: 'Development',
  CONTENT_CREATION: 'Content',
  STUDIO_RENTALS: 'Studio',
  EVENT_PLANNING: 'Events',
};

export default function ServiceCard({ service }) {
  const navigate = useNavigate();
  const { toggle, isFavorite } = useFavoritesStore();
  const { currency } = useCurrencyStore();
  const favorited = isFavorite(service.id);
  const image = service.images?.[0];
  const catColor = CATEGORY_COLORS[service.category] || 'bg-gray-500/20 text-gray-400';
  const catLabel = CATEGORY_LABELS[service.category] || service.category;

  return (
    <div
      className="bg-dark-surface rounded-2xl overflow-hidden card-shadow cursor-pointer hover:scale-[1.02] transition-transform border border-dark-border"
      onClick={() => navigate(`/service/${service.id}`)}
    >
      {/* Image / Video */}
      <div className="relative h-36 bg-dark-muted overflow-hidden">
        {image ? (
          (/\.(mp4|mov|avi|mkv|webm)$/i.test(image) || image?.includes('/video/')) ? (
            <>
              <video src={image} className="w-full h-full object-cover" muted preload="metadata" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                </div>
              </div>
            </>
          ) : (
            <img src={image} alt={service.title} className="w-full h-full object-cover" />
          )
        ) : (
          <div className="w-full h-full bg-brand-gradient opacity-20 flex items-center justify-center">
            <span className="text-4xl opacity-50">🎨</span>
          </div>
        )}
        <span className={`absolute top-2 left-2 text-xs font-semibold px-2 py-1 rounded-full ${catColor}`}>
          {catLabel}
        </span>
        {service.listingType === 'STUDIO' && !service.isFeatured && (
          <span className="absolute top-2 right-2 text-xs font-semibold px-2 py-1 rounded-full bg-brand-purple/80 text-white">
            Studio
          </span>
        )}
        {service.isFeatured && (
          <span className="absolute top-2 right-2 text-xs font-semibold px-2 py-1 rounded-full bg-yellow-500/90 text-black">
            ⭐ Featured
          </span>
        )}
        {/* Heart / favorite button */}
        <button
          onClick={(e) => { e.stopPropagation(); toggle(service); }}
          className="absolute bottom-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-dark-bg/70 backdrop-blur"
          aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
        >
          {favorited ? (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="text-red-500">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          ) : (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-dark-subtext/70">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          )}
        </button>
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className="font-semibold text-sm text-dark-text line-clamp-2 mb-1">{service.title}</h3>
        <div className="flex items-center gap-2 mb-2">
          {service.provider?.avatar ? (
            <img src={service.provider.avatar} className="w-5 h-5 rounded-full object-cover" alt="" />
          ) : (
            <div className="w-5 h-5 rounded-full bg-brand-gradient flex items-center justify-center text-xs text-white font-bold">
              {(service.provider?.fullName || 'P')[0]}
            </div>
          )}
          <span className="text-xs text-dark-subtext truncate">
            {service.provider?.companyName || service.provider?.fullName}
          </span>
          {service.provider?.rating > 0 && (
            <span className="text-xs text-yellow-400 ml-auto flex items-center gap-0.5">
              ⭐ {service.provider.rating.toFixed(1)}
            </span>
          )}
        </div>
        <div className="flex items-baseline justify-between gap-1">
          <span className="text-brand-purple font-bold text-xs leading-tight">
            {service.listingType === 'STUDIO'
              ? `${formatPrice(service.hourlyRate || service.price, currency)}/hr`
              : `From ${formatPrice(service.price, currency)}`}
          </span>
          {service.deliveryDays && (
            <span className="text-[10px] text-dark-subtext whitespace-nowrap">{service.deliveryDays}d delivery</span>
          )}
        </div>
      </div>
    </div>
  );
}
